/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin System KeyPool Health Monitor & Emergency Controls View (Task 11.2.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  KeyRound,
  AlertTriangle,
  RotateCcw,
  Plus,
  RefreshCw,
  CheckCircle2,
  Clock,
  Zap,
  Lock,
  Unlock,
  Trash2,
  Search,
  Check,
  X,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import { DataTable, type ColumnDef } from '../components/table/DataTable';
import { getBrandColor, SophiaIcon } from '@sofia/assets';
import type {
  AdminKeyPoolItem,
  AdminKeyPoolOverview,
  AddKeyPoolKeyInput,
} from '@sofia/shared-types';

export const KeyPoolMonitorView: React.FC = () => {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  // Filters state
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Modals state
  const [isAddKeyOpen, setIsAddKeyOpen] = useState(false);
  const [isResetCircuitOpen, setIsResetCircuitOpen] = useState(false);
  const [quarantiningKey, setQuarantiningKey] = useState<AdminKeyPoolItem | null>(null);
  const [deletingKey, setDeletingKey] = useState<AdminKeyPoolItem | null>(null);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 4000);
  };

  // 1. Fetch KeyPool Overview
  const {
    data: overviewData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['admin-keypool-overview'],
    queryFn: async () => {
      const res = await adminClient.get('/api/v1/admin/keypool');
      return (res?.data || {}) as AdminKeyPoolOverview;
    },
    refetchInterval: 30_000, // Auto-refresh telemetry every 30s
  });

  const keysList: AdminKeyPoolItem[] = overviewData?.keys || [];
  const providersSummary = overviewData?.providers || [];

  // Mutations
  const addKeyMutation = useMutation({
    mutationFn: async (payload: AddKeyPoolKeyInput) => {
      return adminClient.post('/api/v1/admin/keypool', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-keypool-overview'] });
      setIsAddKeyOpen(false);
      showToast('success', t('admin.keypool.successAdd'));
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to add key.');
    },
  });

  const quarantineMutation = useMutation({
    mutationFn: async ({
      provider,
      keyId,
      durationMinutes,
      reason,
    }: {
      provider: string;
      keyId: string;
      durationMinutes: number;
      reason?: string;
    }) => {
      return adminClient.post(`/api/v1/admin/keypool/${provider}/${keyId}/quarantine`, {
        durationMinutes,
        reason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-keypool-overview'] });
      setQuarantiningKey(null);
      showToast('success', t('admin.keypool.successQuarantine'));
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to quarantine key.');
    },
  });

  const releaseMutation = useMutation({
    mutationFn: async ({ provider, keyId }: { provider: string; keyId: string }) => {
      return adminClient.post(`/api/v1/admin/keypool/${provider}/${keyId}/release`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-keypool-overview'] });
      showToast('success', t('admin.keypool.successRelease'));
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to release key.');
    },
  });

  const resetCircuitMutation = useMutation({
    mutationFn: async () => {
      return adminClient.post('/api/v1/admin/keypool/reset-circuit', {});
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin-keypool-overview'] });
      setIsResetCircuitOpen(false);
      const count = data?.data?.resetCount ?? 0;
      showToast('success', `${t('admin.keypool.successReset')} (${count} keys restored)`);
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to reset circuit breaker.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ provider, keyId }: { provider: string; keyId: string }) => {
      return adminClient.delete(`/api/v1/admin/keypool/${provider}/${keyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-keypool-overview'] });
      setDeletingKey(null);
      showToast('success', t('admin.keypool.successDelete'));
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to remove key.');
    },
  });

  // Filtered keys
  const filteredKeys = useMemo(() => {
    return keysList.filter((k) => {
      if (providerFilter !== 'all' && k.provider !== providerFilter) return false;
      if (statusFilter !== 'all' && k.status !== statusFilter) return false;
      if (searchQuery.trim().length > 0) {
        const query = searchQuery.trim().toLowerCase();
        const matchId = k.id.toLowerCase().includes(query);
        const matchKey = k.maskedKey.toLowerCase().includes(query);
        const matchProv = (k.providerName || k.provider).toLowerCase().includes(query);
        if (!matchId && !matchKey && !matchProv) return false;
      }
      return true;
    });
  }, [keysList, providerFilter, statusFilter, searchQuery]);

  // Columns definition
  const columns = useMemo<ColumnDef<AdminKeyPoolItem>[]>(() => [
    {
      id: 'id',
      header: t('admin.keypool.colId'),
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-mono text-xs font-semibold text-foreground">{row.id}</span>
          <span className="text-[10px] text-muted-fg flex items-center gap-1 font-mono">
            <Clock className="w-2.5 h-2.5" />
            {row.lastUsedAt ? new Date(row.lastUsedAt).toLocaleTimeString() : '—'}
          </span>
        </div>
      ),
    },
    {
      id: 'provider',
      header: t('admin.keypool.colProvider'),
      accessorKey: 'provider',
      cell: ({ row }) => {
        const brandColor = getBrandColor(row.provider);
        return (
          <div className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm"
              style={{ backgroundColor: brandColor }}
            />
            <span className="text-xs font-medium text-foreground">
              {row.providerName || row.provider.toUpperCase()}
            </span>
          </div>
        );
      },
    },
    {
      id: 'maskedKey',
      header: t('admin.keypool.colKey'),
      accessorKey: 'maskedKey',
      cell: ({ value }) => (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded font-mono text-xs bg-surface-elevated text-foreground border border-surface-border">
          <Lock className="w-3 h-3 text-muted-fg opacity-60" />
          <span>{value}</span>
        </span>
      ),
    },
    {
      id: 'weight',
      header: t('admin.keypool.colWeight'),
      accessorKey: 'weight',
      cell: ({ value }) => (
        <span className="px-2 py-0.5 rounded text-[11px] font-mono font-bold bg-primary/10 text-primary border border-primary/20">
          {value}x
        </span>
      ),
    },
    {
      id: 'status',
      header: t('admin.keypool.colStatus'),
      accessorKey: 'status',
      cell: ({ row, value }) => {
        const isQuarantined = value === 'quarantined';
        return (
          <div className="flex flex-col gap-1">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium border w-fit ${
                isQuarantined
                  ? 'bg-destructive/10 text-destructive border-destructive/20 animate-pulse'
                  : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
              }`}
            >
              {isQuarantined ? (
                <>
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span>{t('admin.keypool.statusQuarantined')}</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>{t('admin.keypool.statusActive')}</span>
                </>
              )}
            </span>
            {isQuarantined && row.quarantinedUntil && (
              <span className="text-[10px] text-destructive/80 font-mono">
                until {new Date(row.quarantinedUntil).toLocaleTimeString()}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'successRate',
      header: t('admin.keypool.colSuccessRate'),
      accessorKey: 'successRate',
      cell: ({ value }) => {
        const rate = Number(value);
        const colorClass =
          rate >= 99
            ? 'text-emerald-500'
            : rate >= 95
            ? 'text-amber-500'
            : 'text-destructive';

        return (
          <div className="flex flex-col gap-1 min-w-[90px]">
            <span className={`text-xs font-mono font-bold ${colorClass}`}>{rate}%</span>
            <div className="w-full h-1.5 rounded-full bg-surface-border overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  rate >= 99
                    ? 'bg-emerald-500'
                    : rate >= 95
                    ? 'bg-amber-500'
                    : 'bg-destructive'
                }`}
                style={{ width: `${Math.min(100, Math.max(0, rate))}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      id: 'rateLimit429Count1h',
      header: t('admin.keypool.col429s'),
      accessorKey: 'rateLimit429Count1h',
      cell: ({ value }) => (
        <span
          className={`font-mono text-xs font-bold ${
            value > 0 ? 'text-destructive' : 'text-muted-fg'
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      id: 'actions',
      header: t('admin.keypool.colActions'),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          {row.status === 'quarantined' ? (
            <button
              type="button"
              onClick={() => releaseMutation.mutate({ provider: row.provider, keyId: row.id })}
              disabled={releaseMutation.isPending}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
              title={t('admin.keypool.releaseBtn')}
            >
              <Unlock className="w-3.5 h-3.5" />
              <span>{t('admin.keypool.releaseBtn')}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setQuarantiningKey(row)}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors"
              title={t('admin.keypool.quarantineBtn')}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>{t('admin.keypool.quarantineBtn')}</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => setDeletingKey(row)}
            className="p-1.5 rounded-lg border border-surface-border bg-surface hover:bg-destructive/10 text-muted-fg hover:text-destructive hover:border-destructive/30 transition-colors"
            title={t('admin.keypool.deleteBtn')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ], [t, releaseMutation.isPending]);

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-xs font-medium border animate-in fade-in slide-in-from-top-3 ${
            toastMessage.type === 'success'
              ? 'bg-emerald-950/90 text-emerald-200 border-emerald-500/30'
              : 'bg-destructive/90 text-destructive-foreground border-destructive'
          }`}
        >
          {toastMessage.type === 'success' ? (
            <Check className="w-4 h-4 text-emerald-400" />
          ) : (
            <AlertTriangle className="w-4 h-4" />
          )}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 rounded-2xl bg-surface border border-surface-border relative overflow-hidden">
        <div className="space-y-1 z-10">
          <div className="flex items-center gap-2 text-primary text-xs font-mono font-semibold uppercase tracking-wider">
            <KeyRound className="w-4 h-4" />
            <span>Cloudflare KV Engine (`SYSTEM_KEYS_KV`)</span>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground">
            {t('admin.keypool.title')}
          </h2>
          <p className="text-xs text-muted-fg max-w-xl">
            {t('admin.keypool.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2 z-10">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated text-foreground transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            <span>{t('admin.keypool.refresh')}</span>
          </button>

          <button
            type="button"
            onClick={() => setIsResetCircuitOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
            title={t('admin.keypool.circuitReset')}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>{t('admin.keypool.circuitReset')}</span>
          </button>

          <button
            type="button"
            onClick={() => setIsAddKeyOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-fg hover:bg-primary/90 shadow-glow-primary transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>{t('admin.keypool.addKey')}</span>
          </button>
        </div>

        {/* Ambient background decoration */}
        <div className="absolute right-0 top-0 w-80 h-full bg-gradient-to-l from-primary/5 via-accent/5 to-transparent pointer-events-none" />
      </div>

      {/* Health KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-surface border border-surface-border flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-fg">
              {t('admin.keypool.totalKeys')}
            </span>
            <div className="text-2xl font-heading font-bold text-foreground">
              {overviewData?.totalKeys ?? 0}
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-surface-elevated border border-surface-border flex items-center justify-center text-primary">
            <SophiaIcon name="big-data" size={20} className="text-primary" />
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-surface border border-surface-border flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-fg">
              {t('admin.keypool.activeKeys')}
            </span>
            <div className="text-2xl font-heading font-bold text-emerald-500">
              {overviewData?.activeKeys ?? 0}
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500">
            <SophiaIcon name="server" size={20} className="text-emerald-500" />
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-surface border border-surface-border flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-fg">
              {t('admin.keypool.quarantinedKeys')}
            </span>
            <div className="text-2xl font-heading font-bold text-destructive">
              {overviewData?.quarantinedKeys ?? 0}
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-destructive/10 border border-destructive/20 flex items-center justify-center text-destructive">
            <SophiaIcon name="filter" size={20} className="text-destructive" />
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-surface border border-surface-border flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-fg">
              {t('admin.keypool.rateLimitErrors')}
            </span>
            <div className="text-2xl font-heading font-bold text-amber-500">
              {overviewData?.total429s1h ?? 0}
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500">
            <Zap className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Provider Status Pill Cards */}
      <div className="p-4 rounded-xl bg-surface border border-surface-border space-y-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-fg">
          Provider Pools Telemetry
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 xl:grid-cols-9 gap-2">
          {providersSummary.map((prov) => {
            const brandColor = getBrandColor(prov.provider);
            const isDegraded = prov.quarantinedKeys > 0;
            return (
              <div
                key={prov.provider}
                onClick={() => setProviderFilter(prov.provider === providerFilter ? 'all' : prov.provider)}
                className={`p-2.5 rounded-xl border cursor-pointer transition-all flex flex-col justify-between ${
                  providerFilter === prov.provider
                    ? 'bg-primary/10 border-primary ring-1 ring-primary'
                    : 'bg-surface-elevated border-surface-border hover:border-primary/40'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: brandColor }}
                    />
                    <span className="text-[11px] font-semibold text-foreground truncate max-w-[70px]">
                      {prov.providerName}
                    </span>
                  </div>
                  {isDegraded ? (
                    <span className="w-2 h-2 rounded-full bg-destructive animate-ping" />
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  )}
                </div>
                <div className="flex items-baseline justify-between mt-2 pt-1 border-t border-surface-border text-[10px] text-muted-fg font-mono">
                  <span>{prov.activeKeys}/{prov.totalKeys} keys</span>
                  <span className={prov.avgSuccessRate >= 99 ? 'text-emerald-500' : 'text-amber-500'}>
                    {prov.avgSuccessRate}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="p-4 rounded-xl bg-surface border border-surface-border flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-muted-fg absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('admin.keypool.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg focus:outline-none focus:border-primary/50"
          />
        </div>

        {/* Provider Filter */}
        <select
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          className="px-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
        >
          <option value="all">{t('admin.keypool.filterAllProviders')}</option>
          {providersSummary.map((p) => (
            <option key={p.provider} value={p.provider}>
              {p.providerName} ({p.totalKeys})
            </option>
          ))}
        </select>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
        >
          <option value="all">{t('admin.keypool.filterAllStatuses')}</option>
          <option value="active">{t('admin.keypool.statusActive')}</option>
          <option value="quarantined">{t('admin.keypool.statusQuarantined')}</option>
        </select>
      </div>

      {/* TanStack Data Table */}
      <div className="bg-surface rounded-2xl border border-surface-border overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredKeys}
          isLoading={isLoading}
          error={error ? t('admin.keypool.errorLoading') : null}
          enableSearch={false}
          enableCsvExport={true}
          exportFileName="sophia_system_keys_pool.csv"
        />
      </div>

      {/* Add Key Modal */}
      {isAddKeyOpen && (
        <AddKeyModal
          isOpen={true}
          isLoading={addKeyMutation.isPending}
          onClose={() => setIsAddKeyOpen(false)}
          onAdd={(payload) => addKeyMutation.mutate(payload)}
        />
      )}

      {/* Quarantine Modal */}
      {quarantiningKey && (
        <QuarantineModal
          keyItem={quarantiningKey}
          isOpen={true}
          isLoading={quarantineMutation.isPending}
          onClose={() => setQuarantiningKey(null)}
          onConfirm={(duration, reason) =>
            quarantineMutation.mutate({
              provider: quarantiningKey.provider,
              keyId: quarantiningKey.id,
              durationMinutes: duration,
              reason,
            })
          }
        />
      )}

      {/* Emergency Circuit Reset Confirmation Modal */}
      {isResetCircuitOpen && (
        <CircuitResetModal
          isLoading={resetCircuitMutation.isPending}
          onClose={() => setIsResetCircuitOpen(false)}
          onConfirm={() => resetCircuitMutation.mutate()}
        />
      )}

      {/* Delete Key Modal */}
      {deletingKey && (
        <DeleteKeyModal
          keyItem={deletingKey}
          isLoading={deleteMutation.isPending}
          onClose={() => setDeletingKey(null)}
          onConfirm={() =>
            deleteMutation.mutate({ provider: deletingKey.provider, keyId: deletingKey.id })
          }
        />
      )}
    </div>
  );
};

// --- Sub-Component: Add Key Modal ---
interface AddKeyModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onAdd: (payload: AddKeyPoolKeyInput) => void;
}

const AddKeyModal: React.FC<AddKeyModalProps> = ({ isLoading, onClose, onAdd }) => {
  const { t } = useI18n();

  const [provider, setProvider] = useState('openai');
  const [key, setKey] = useState('');
  const [weight, setWeight] = useState(1);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      provider,
      key: key.trim(),
      weight: Number(weight),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-lg bg-surface border border-surface-border rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated">
          <div className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-heading font-bold text-foreground">
              {t('admin.keypool.modalAddTitle')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.keypool.fieldProvider')}
            </label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
            >
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic</option>
              <option value="gemini">Google Gemini</option>
              <option value="deepseek">DeepSeek</option>
              <option value="fal">Fal.ai</option>
              <option value="kling">Kling Video</option>
              <option value="elevenlabs">ElevenLabs</option>
              <option value="stability">Stability AI</option>
              <option value="modelslab">ModelsLab AI</option>
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.keypool.fieldKey')}
            </label>
            <input
              type="password"
              required
              placeholder="sk-proj-... / sk-ant-..."
              value={key}
              onChange={(e) => setKey(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground font-mono focus:outline-none focus:border-primary"
            />
            <p className="text-[10px] text-muted-fg mt-1">
              {t('admin.keypool.fieldKeyHelp')}
            </p>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.keypool.fieldWeight')}
            </label>
            <input
              type="number"
              min="1"
              max="10"
              required
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value))}
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-xs font-medium rounded-xl border border-surface-border text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
            >
              {t('admin.keypool.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-fg hover:bg-primary/90 shadow-glow-primary transition-colors disabled:opacity-50"
            >
              {isLoading ? t('admin.keypool.saving') : t('admin.keypool.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Sub-Component: Quarantine Key Modal ---
interface QuarantineModalProps {
  keyItem: AdminKeyPoolItem;
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: (duration: number, reason?: string) => void;
}

const QuarantineModal: React.FC<QuarantineModalProps> = ({
  keyItem,
  isLoading,
  onClose,
  onConfirm,
}) => {
  const { t } = useI18n();
  const [duration, setDuration] = useState(15);
  const [reason, setReason] = useState('Manual traffic diversion / diagnostic check');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-md bg-surface border border-surface-border rounded-2xl shadow-2xl overflow-hidden p-6 space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500 mx-auto">
          <AlertTriangle className="w-6 h-6" />
        </div>

        <div className="text-center space-y-1">
          <h3 className="text-base font-heading font-bold text-foreground">
            {t('admin.keypool.modalQuarantineTitle')}
          </h3>
          <p className="text-xs text-muted-fg">
            Quarantine key <span className="font-mono text-foreground">{keyItem.id}</span> ({keyItem.provider})
          </p>
        </div>

        <div className="space-y-3 pt-2">
          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.keypool.fieldDuration')}
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
            >
              <option value="5">5 Minutes</option>
              <option value="15">15 Minutes</option>
              <option value="30">30 Minutes</option>
              <option value="60">1 Hour</option>
              <option value="180">3 Hours</option>
              <option value="1440">24 Hours</option>
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.keypool.fieldReason')}
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-xs font-medium rounded-xl border border-surface-border text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
          >
            {t('admin.keypool.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(duration, reason)}
            disabled={isLoading}
            className="px-5 py-2 text-xs font-semibold rounded-xl bg-amber-500 text-black hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            {isLoading ? t('admin.keypool.saving') : t('admin.keypool.quarantineBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Sub-Component: Circuit Reset Modal ---
interface CircuitResetModalProps {
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const CircuitResetModal: React.FC<CircuitResetModalProps> = ({
  isLoading,
  onClose,
  onConfirm,
}) => {
  const { t } = useI18n();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-md bg-surface border border-surface-border rounded-2xl shadow-2xl overflow-hidden p-6 space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center text-destructive mx-auto">
          <RotateCcw className="w-6 h-6" />
        </div>

        <div className="text-center space-y-1">
          <h3 className="text-base font-heading font-bold text-foreground">
            {t('admin.keypool.confirmCircuitResetTitle')}
          </h3>
          <p className="text-xs text-muted-fg">
            {t('admin.keypool.confirmCircuitResetDesc')}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-xs font-medium rounded-xl border border-surface-border text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
          >
            {t('admin.keypool.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-5 py-2 text-xs font-semibold rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
          >
            {isLoading ? t('admin.keypool.saving') : t('admin.keypool.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Sub-Component: Delete Key Modal ---
interface DeleteKeyModalProps {
  keyItem: AdminKeyPoolItem;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteKeyModal: React.FC<DeleteKeyModalProps> = ({
  keyItem,
  isLoading,
  onClose,
  onConfirm,
}) => {
  const { t } = useI18n();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-md bg-surface border border-surface-border rounded-2xl shadow-2xl overflow-hidden p-6 space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center text-destructive mx-auto">
          <Trash2 className="w-6 h-6" />
        </div>

        <div className="text-center space-y-1">
          <h3 className="text-base font-heading font-bold text-foreground">
            {t('admin.keypool.deleteBtn')}
          </h3>
          <p className="text-xs text-muted-fg">
            Permanently delete key <span className="font-mono text-foreground">{keyItem.id}</span> ({keyItem.maskedKey}) from provider pool <span className="font-semibold text-foreground">{keyItem.provider}</span>.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-xs font-medium rounded-xl border border-surface-border text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
          >
            {t('admin.keypool.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-5 py-2 text-xs font-semibold rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
          >
            {isLoading ? t('admin.keypool.saving') : t('admin.keypool.deleteBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};
